/**
 * Responsibility: Implement combat behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { CombatState, CombatFoeState, Vessel, NPC, GameState, NpcLogicUpdate, ActionOption, CombatLogEntry } from "@anyquest/shared-core"
import { CombatStatus, ActionCategory } from "@anyquest/shared-core"
import { NpcActor } from './entities'
// ============================================================
// COMBAT.TS — Turn-based combat helpers
// "Code is Law" split: Logic Forge handles player-side effects
// (vitality damage taken, spirit cost, etc.) while this module
// tracks NPC vitality and resolves enemy counterattacks.
// ============================================================

// NPC gets the opener only on round 1 and only if initiative lead is not player.
export function shouldNpcActFirst(combat: CombatState): boolean {
  return combat.round === 1 && (combat.initiative?.[0] ?? 'player') !== 'player'
}

// ---- NPC attacks the player ----
// Returns vitality damage dealt (0 on miss, capped to avoid 1-shot).
export function npcAttackDamage(npc: NPC, d20Roll: number): number {
  return NpcActor.fromDTO(npc).attackDamage(d20Roll)
}

// ---- Player damages the NPC ----
// Derived from successLevel + stat bonus. Returns NPC vitality reduction.
export function calcNpcDamage(successLevel: number, statVal: number): number {
  if (successLevel === 0) return 0
  const statBonus = Math.max(0, Math.floor((statVal - 10) / 2))
  const roll = Math.floor(Math.random() * 16) + 5; // 5–20
  const base = successLevel === 2 ? 28 : 14
  return base + statBonus + Math.floor(roll / 3)
}

// ---- Start a new combat engagement ----
export function startCombat(
  npc: NPC,
  player: Vessel,
  allyNames: string[] = [],
  foeNames: string[] = [],
): CombatState {
  const playerGoesFirst = player.attributes.DEX.val > NpcActor.fromDTO(npc).inferDex()
  const uniqueAllies = [...new Set((allyNames ?? []).map(v => String(v ?? '').trim()).filter(Boolean))]
  const uniqueFoes = [...new Set([npc.name, ...(foeNames ?? [])].map(v => String(v ?? '').trim()).filter(Boolean))]
  const turnQueue = playerGoesFirst
    ? ['player', ...uniqueAllies, ...uniqueFoes]
    : [uniqueFoes[0] ?? npc.name, 'player', ...uniqueAllies, ...uniqueFoes.slice(1)]
  const foes: CombatFoeState[] = uniqueFoes.map(name => ({ name, vitality: 100 }))
  const activeTargetName = foes[0]?.name ?? npc.name
  return {
    npc,
    round: 1,
    initiative: turnQueue,
    npcVitality: 100,
    playerAtStart: player.vitals.vitality,
    status: CombatStatus.active,
    log: [],
    foes,
    allies: uniqueAllies,
    activeTargetName,
    turnQueue,
    turnCursor: 0,
  }
}

// ---- Get available actions for the player in combat ----
export function getActionOptions(player: Vessel): ActionOption[] {
  const options: ActionOption[] = [{ id: 'attack', label: 'Attack', type: 'attack' }]
  player.abilities.forEach(ability => {
    options.push({ id: ability.id, label: ability.name, type: 'ability' })
  })
  return options
}

// ---- Player turn: apply damage to NPC, return updated combat ----
export function processPlayerTurn(
  combat: CombatState,
  input: string,
  successLevel: number,
  statVal: number,
  isOffensive: boolean,
  actor: 'player' | 'companion' = 'player',
  actorName?: string,
  targetName?: string,
): { combat: CombatState; npcDamage: number } {
  const target = resolveFoeTarget(combat, targetName)
  const npcDamage = isOffensive ? calcNpcDamage(successLevel, statVal) : 0
  let foes = listCombatFoes(combat)
  if (isOffensive && target) {
    foes = foes.map(foe =>
      foe.name === target.name
        ? { ...foe, vitality: Math.max(0, foe.vitality - npcDamage) }
        : foe
    )
  }

  const aliveFoes = foes.filter(foe => foe.vitality > 0)
  const nextTargetName = resolveNextTargetName(foes, target?.name ?? combat.activeTargetName)
  const activeTarget = foes.find(foe => foe.name === nextTargetName) ?? target ?? foes[0]
  const nextNpcVitality = activeTarget?.vitality ?? combat.npcVitality
  const entry: CombatLogEntry = {
    round: combat.round,
    actor,
    ...(actorName ? { actorName } : {}),
    action: input,
    outcome: isOffensive 
      ? (npcDamage > 0
        ? `Hit ${target?.name ?? 'target'}! ~${npcDamage} damage dealt`
        : `Missed or ineffective against ${target?.name ?? 'target'}.`)
      : (successLevel > 0 ? 'Action successful.' : 'Action failed.'),
  }
  const updated: CombatState = {
    ...combat,
    foes,
    activeTargetName: nextTargetName,
    npcVitality: nextNpcVitality,
    status: aliveFoes.length === 0 ? CombatStatus.victory : CombatStatus.active,
    log: [...combat.log, entry],
  }
  return { combat: updated, npcDamage }
}

// ---- Enemy turn: NPC attacks back via Logic Forge ----
// Returns { combat, npcResult } — caller applies all NpcLogicUpdate effects.
export async function processNpcTurn(
  combat: CombatState,
  state: GameState,
): Promise<{ combat: CombatState; npcResult: NpcLogicUpdate }> {
  const activeFoes = listCombatFoes(combat).filter(foe => foe.vitality > 0)
  const queue = combat.turnQueue?.length ? combat.turnQueue : combat.initiative
  const allySet = new Set((combat.allies ?? []).map(v => String(v ?? '').trim().toLowerCase()))
  const enemySet = new Set(activeFoes.map(foe => foe.name.toLowerCase()))
  const queueLen = queue.length || 1
  const cursor = Number.isFinite(combat.turnCursor) ? Math.max(0, combat.turnCursor as number) : 0
  let attackerName = activeFoes[0]?.name ?? combat.activeTargetName ?? combat.npc.name
  let selectedIdx = cursor % queueLen
  for (let i = 0; i < queueLen; i++) {
    const idx = (cursor + i) % queueLen
    const token = String(queue[idx] ?? '').trim()
    const normalized = token.toLowerCase()
    if (!token || normalized === 'player' || allySet.has(normalized)) continue
    if (!enemySet.has(normalized)) continue
    attackerName = token
    selectedIdx = idx
    break
  }

  const attackerNpc = resolveNpcByName(state, attackerName) ?? combat.npc
  const d20 = Math.floor(Math.random() * 20) + 1
  const { resolveNpcAttack } = await import('./npc-logic')
  const npcResult = await resolveNpcAttack(attackerNpc, state, d20)
  const entry: CombatLogEntry = {
    round: combat.round,
    actor: 'npc',
    actorName: attackerNpc.name,
    action: `${attackerNpc.name} attacks`,
    outcome: npcResult.outcome,
  }
  const nextCursor = (selectedIdx + 1) % queueLen
  const wrapped = nextCursor <= selectedIdx
  const nextTargetName = resolveNextTargetName(activeFoes, combat.activeTargetName ?? attackerNpc.name)
  const nextTarget = activeFoes.find(foe => foe.name === nextTargetName) ?? activeFoes[0]
  const updated: CombatState = {
    ...combat,
    npc: attackerNpc,
    activeTargetName: nextTargetName,
    npcVitality: nextTarget?.vitality ?? combat.npcVitality,
    turnCursor: nextCursor,
    round: combat.round + (wrapped ? 1 : 0),
    log: [...combat.log, entry],
  }
  return { combat: updated, npcResult }
}

// ---- Flee: end combat, player escapes ----
export function fleeCombat(combat: CombatState): CombatState {
  return { ...combat, status: CombatStatus.fled }
}

export function listCombatFoes(combat: CombatState): CombatFoeState[] {
  const foes = (combat.foes ?? [])
    .map(foe => ({
      name: String(foe?.name ?? '').trim(),
      vitality: Number.isFinite(foe?.vitality) ? Math.max(0, Math.min(100, Number(foe.vitality))) : 0,
    }))
    .filter(foe => !!foe.name)
  if (foes.length) return foes
  const fallbackName = String(combat.npc?.name ?? '').trim() || 'Enemy'
  return [{ name: fallbackName, vitality: Math.max(0, Math.min(100, Number(combat.npcVitality ?? 100))) }]
}

export function getActiveTargetName(combat: CombatState): string {
  return resolveNextTargetName(listCombatFoes(combat), combat.activeTargetName ?? combat.npc?.name) ?? combat.npc?.name ?? 'Enemy'
}

export function setCombatTarget(combat: CombatState, targetName: string): CombatState {
  const target = resolveFoeTarget(combat, targetName)
  if (!target || target.vitality <= 0) return combat
  return {
    ...combat,
    activeTargetName: target.name,
    npcVitality: target.vitality,
  }
}

export function peekNextCombatActor(combat: CombatState): 'player' | string {
  const queue = combat.turnQueue?.length ? combat.turnQueue : combat.initiative
  if (!queue.length) return 'player'
  const cursor = Number.isFinite(combat.turnCursor) ? Math.max(0, Number(combat.turnCursor)) : 0
  const token = String(queue[cursor % queue.length] ?? '').trim()
  return (token || 'player') as 'player' | string
}

export function advanceCombatQueueCursor(combat: CombatState, steps = 1): CombatState {
  const queue = combat.turnQueue?.length ? combat.turnQueue : combat.initiative
  const queueLen = Math.max(1, queue.length)
  const safeSteps = Math.max(0, Math.floor(steps))
  const current = Number.isFinite(combat.turnCursor) ? Math.max(0, Number(combat.turnCursor)) : 0
  const nextRaw = current + safeSteps
  const wrapped = Math.floor(nextRaw / queueLen) > Math.floor(current / queueLen)
  return {
    ...combat,
    turnCursor: nextRaw % queueLen,
    round: combat.round + (wrapped ? 1 : 0),
  }
}

// ---- Helpers ----

export function isFlee(input: string): boolean {
  return /\b(flee|run away|escape|retreat|back away|withdraw|disengage)\b/i.test(input)
}

export function isAttackAction(category: ActionCategory | undefined): boolean {
  return category === ActionCategory.attack
}

export function normalizeCombatTargetName(input: string | null | undefined): string {
  return String(input ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, '')
    .replace(/^the\s+/i, '')
    .replace(/\s+/g, ' ')
}

// Get the best NPC to fight (prefer named opponents, fall back to first)
export function pickOpponent(npcs: NPC[], targetName?: string): NPC | null {
  if (!npcs.length) return null
  const normalizedTarget = normalizeCombatTargetName(targetName)
  if (normalizedTarget) {
    const match = npcs.find(n => {
      const normalizedName = normalizeCombatTargetName(n.name)
      return normalizedName.includes(normalizedTarget) || normalizedTarget.includes(normalizedName)
    })
    if (match) return match
  }
  return npcs[0]
}

// Extract NPC name from attack input (e.g. "Attack the goblin" → "goblin")
export function extractTargetName(input: string): string | undefined {
  const m = input.match(/\b(?:attack|fight|strike|hit|stab|shoot|slash)\s+(?:the\s+)?(.+)/i)
  const normalized = normalizeCombatTargetName(m?.[1])
  return normalized || undefined
}

function resolveFoeTarget(combat: CombatState, targetName?: string): CombatFoeState | undefined {
  const foes = listCombatFoes(combat)
  if (!foes.length) return undefined
  const normalizedTarget = normalizeCombatTargetName(targetName)
  if (normalizedTarget) {
    const named = foes.find(foe => {
      const normalizedName = normalizeCombatTargetName(foe.name)
      return normalizedName.includes(normalizedTarget) || normalizedTarget.includes(normalizedName)
    })
    if (named) return named
  }
  const activeTarget = normalizeCombatTargetName(combat.activeTargetName)
  if (activeTarget) {
    const active = foes.find(foe => normalizeCombatTargetName(foe.name) === activeTarget && foe.vitality > 0)
    if (active) return active
  }
  return foes.find(foe => foe.vitality > 0) ?? foes[0]
}

function resolveNextTargetName(foes: CombatFoeState[], preferredName?: string): string | undefined {
  const normalizedPreferred = normalizeCombatTargetName(preferredName)
  if (normalizedPreferred) {
    const exact = foes.find(foe => normalizeCombatTargetName(foe.name) === normalizedPreferred && foe.vitality > 0)
    if (exact) return exact.name
  }
  return foes.find(foe => foe.vitality > 0)?.name
}

function resolveNpcByName(state: GameState, npcName: string): NPC | null {
  const wanted = normalizeCombatTargetName(npcName)
  if (!wanted) return null
  const roomNpc = (state.currentRoom.npcs ?? []).find(npc => normalizeCombatTargetName(npc.name) === wanted)
  return roomNpc ?? null
}
